import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CreditCard,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Loader2,
  Receipt,
} from 'lucide-react';
import {
  getUserSubscriptions,
  getPaymentHistory,
  cancelSubscription,
  type Subscription,
  type Payment,
} from '../../api/payment';

const SubscriptionTab: React.FC = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancellingModule, setCancellingModule] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Prevent double fetch in React.StrictMode (development)
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [subs, pays] = await Promise.all([
        getUserSubscriptions(),
        getPaymentHistory(),
      ]);
      setSubscriptions(subs);
      setPayments(pays);
    } catch (err) {
      setError('Veriler yüklenirken bir hata oluştu');
      console.error('Failed to load subscription data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async (moduleType: string) => {
    if (!window.confirm('Aboneliğinizi iptal etmek istediğinizden emin misiniz? Mevcut dönem sonuna kadar kullanmaya devam edebilirsiniz.')) {
      return;
    }

    try {
      setCancellingModule(moduleType);
      await cancelSubscription(moduleType);
      await loadData();
    } catch (err) {
      setError('Abonelik iptal edilemedi');
      console.error('Failed to cancel subscription:', err);
    } finally {
      setCancellingModule(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Aktif
          </span>
        );
      case 'trial':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            <Clock className="w-3 h-3" />
            Deneme
          </span>
        );
      case 'canceled':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <AlertCircle className="w-3 h-3" />
            İptal Edildi
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Süresi Doldu
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            <Clock className="w-3 h-3" />
            Beklemede
          </span>
        );
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3 h-3" />
            Başarılı
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="w-3 h-3" />
            Başarısız
          </span>
        );
      case 'refunded':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
            <RefreshCw className="w-3 h-3" />
            İade
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="w-3 h-3" />
            Beklemede
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const moduleNames: Record<string, string> = {
    mail: 'E-Posta Modülü',
    template: 'Şablon Modülü',
    bundle: 'Avukasa Ofis',
  };

  const tierNames: Record<string, string> = {
    standard: 'Standart',
    pro: 'Profesyonel',
    unlimited: 'Sınırsız',
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Active Subscriptions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-blue-600" />
            Aboneliklerim
          </h3>
          <button
            onClick={() => navigate('/subscription')}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
          >
            Plan Değiştir
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {subscriptions.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-gray-900 font-medium mb-2">
              Aktif Abonelik Yok
            </h4>
            <p className="text-gray-500 mb-4">
              Henüz bir aboneliğiniz bulunmuyor. Tüm özelliklere erişmek için bir plan seçin.
            </p>
            <button
              onClick={() => navigate('/subscription')}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Planları İncele
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-semibold text-gray-900">
                        {moduleNames[sub.module_type] || sub.module_type}
                      </h4>
                      {getStatusBadge(sub.status)}
                    </div>
                    <p className="text-gray-500">
                      {tierNames[sub.tier] || sub.tier} •{' '}
                      {sub.billing_cycle === 'yearly' ? 'Yıllık' : 'Aylık'}
                    </p>
                  </div>
                  <div className="text-right">
                    {sub.is_active && sub.days_left > 0 && (
                      <div className="text-sm text-gray-500 mb-1">
                        {sub.days_left} gün kaldı
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      Bitiş: {formatDate(sub.end_date)}
                    </div>
                  </div>
                </div>

                {/* Progress Bar for remaining days */}
                {sub.is_active && sub.days_left > 0 && (
                  <div className="mt-4">
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all"
                        style={{
                          width: `${Math.min(100, (sub.days_left / 30) * 100)}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                {sub.status === 'active' && (
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      Otomatik yenileme: {sub.auto_renew ? 'Açık' : 'Kapalı'}
                    </div>
                    <button
                      onClick={() => handleCancelSubscription(sub.module_type)}
                      disabled={cancellingModule === sub.module_type}
                      className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                    >
                      {cancellingModule === sub.module_type ? (
                        <span className="flex items-center gap-1">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          İptal Ediliyor...
                        </span>
                      ) : (
                        'Aboneliği İptal Et'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment History */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Receipt className="w-5 h-5 text-green-600" />
          Ödeme Geçmişi
        </h3>

        {payments.length === 0 ? (
          <div className="bg-gray-50 rounded-xl p-8 text-center">
            <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Henüz ödeme geçmişiniz bulunmuyor.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plan
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tutar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {moduleNames[payment.module_type] || payment.module_type} -{' '}
                      {tierNames[payment.tier] || payment.tier}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {payment.amount.toFixed(2)} {payment.currency}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getPaymentStatusBadge(payment.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionTab;

